import { useState } from "react";
import logoImg from "@/assets/logo.png";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AccountDialog from "@/components/common/AccountDialog";
import { LogOut, User, Award, Home, CheckCircle, Users, ClipboardCheck, Loader2, Settings, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

const DashboardLayout = ({ children }: { children: React.ReactNode }) => {
  const { role, user, fullName, signOut } = useAuth();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
  };

  const firstName = fullName ? fullName.split(" ")[0] : "";

  const navItems: NavItem[] = (() => {
    switch (role) {
      case "student":
        return [
          { label: "Dashboard", href: "/student", icon: <Home className="h-4 w-4" /> },
          { label: "My Certificates", href: "/student/certificates", icon: <Award className="h-4 w-4" /> },
          { label: "Profile", href: "/student/profile", icon: <User className="h-4 w-4" /> },
        ];
      case "growlearn":
        return [
          { label: "Dashboard", href: "/growlearn", icon: <Home className="h-4 w-4" /> },
          { label: "Students", href: "/growlearn/students", icon: <Users className="h-4 w-4" /> },
          { label: "Failed Students", href: "/growlearn/failed", icon: <ClipboardCheck className="h-4 w-4" /> },
          { label: "Manage Users", href: "/growlearn/users", icon: <Users className="h-4 w-4" /> },
          { label: "Settings", href: "/growlearn/settings", icon: <Settings className="h-4 w-4" /> },
        ];
      case "aca_member":
        return [
          { label: "Dashboard", href: "/aca", icon: <Home className="h-4 w-4" /> },
          { label: "Review Queue", href: "/aca/review", icon: <ClipboardCheck className="h-4 w-4" /> },
          { label: "Approved", href: "/aca/approved", icon: <CheckCircle className="h-4 w-4" /> },
          { label: "Settings", href: "/aca/settings", icon: <Settings className="h-4 w-4" /> },
        ];
      case "admin":
        return [
          { label: "Dashboard", href: "/admin", icon: <Home className="h-4 w-4" /> },
          { label: "Students", href: "/admin/students", icon: <Users className="h-4 w-4" /> },
          { label: "Manage Users", href: "/admin/users", icon: <Users className="h-4 w-4" /> },
        ];
      default:
        return [];
    }
  })();

  const roleLabel = role === "student" ? "Student Portal" : role === "growlearn" ? "GrowLearn Portal" : role === "admin" ? "Admin Portal" : role === "aca_member" ? "ACA Member Portal" : "";

  const initials = fullName
    ? fullName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : user?.email?.substring(0, 2).toUpperCase() || "U";

  // Top-bar title: the current nav item's label (exact, else longest prefix match), else portal name.
  const pageTitle = (() => {
    const exact = navItems.find((i) => i.href === location.pathname);
    if (exact) return exact.label;
    const prefix = navItems
      .filter((i) => location.pathname.startsWith(i.href) && i.href !== "/")
      .sort((a, b) => b.href.length - a.href.length)[0];
    return prefix?.label || roleLabel || "Dashboard";
  })();

  return (
    <div className="flex h-screen">
      <aside
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        className="hidden md:flex flex-col shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out"
        style={{ width: expanded ? "256px" : "64px", background: "linear-gradient(180deg, #6b7ee6 0%, #7450a9 100%)" }}
      >
        {/* Logo */}
        <div className={cn("flex items-center overflow-hidden border-b border-white/10 py-5", expanded ? "gap-3 px-3" : "justify-center")}>
          <div className="shrink-0 rounded-lg bg-white p-1">
            <img src={logoImg} alt="ACA" className="h-7 w-7 rounded object-contain" />
          </div>
          {expanded && (
            <div className="overflow-hidden whitespace-nowrap">
              <h1 className="text-sm font-bold text-sidebar-foreground">ACA System</h1>
              <p className="text-xs text-sidebar-foreground/60">{roleLabel}</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 p-2 overflow-hidden">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              title={item.label}
              className={cn(
                "flex items-center rounded-xl text-sm font-medium transition-colors duration-150",
                expanded ? "gap-3 px-3 py-2.5" : "mx-auto h-11 w-11 justify-center",
                location.pathname === item.href
                  ? "bg-white text-primary shadow-sm"
                  : "text-white/70 hover:bg-white/15 hover:text-white"
              )}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">{item.icon}</span>
              {expanded && <span className="whitespace-nowrap">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* User */}
        <div className="border-t border-white/10 p-3 overflow-hidden">
          <button
            type="button"
            onClick={() => setAccountOpen(true)}
            title="My Account"
            className={cn(
              "mb-2 flex w-full items-center rounded-xl p-1 text-left transition-colors duration-150 hover:bg-white/10",
              expanded ? "gap-3" : "justify-center"
            )}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full gradient-warm text-xs font-bold text-white shadow-sm">
              {initials}
            </div>
            {expanded && (
              <div className="overflow-hidden whitespace-nowrap">
                <p className="truncate text-xs font-medium text-sidebar-foreground/90">{fullName || ""}</p>
                <p className="truncate text-xs text-sidebar-foreground/50">{user?.email}</p>
              </div>
            )}
          </button>
          <Button
            variant="ghost"
            size="sm"
            className={cn("overflow-hidden text-sidebar-foreground/60 transition-colors duration-150 hover:bg-white/10 hover:text-white", expanded ? "w-full justify-start" : "w-full justify-center")}
            onClick={handleSignOut}
            disabled={signingOut}
          >
            <span className="shrink-0">
              {signingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
            </span>
            {expanded && (
              <span className="ml-2 whitespace-nowrap">
                {signingOut ? "Signing out..." : "Sign Out"}
              </span>
            )}
          </Button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Desktop top bar */}
        <header className="hidden shrink-0 items-center justify-between border-b border-border bg-card/80 px-6 py-3 backdrop-blur md:flex">
          <div className="flex items-center gap-2.5">
            <span className="h-5 w-1 rounded-full bg-primary" />
            <h1 className="text-lg font-bold tracking-tight text-foreground">{pageTitle}</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary">
              <Calendar className="h-3.5 w-3.5" /> {format(new Date(), "EEE, MMM d")}
            </span>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50/60 px-3.5 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:opacity-60"
            >
              {signingOut ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
              {signingOut ? "Logging out…" : "Logout"}
            </button>
          </div>
        </header>

        <header className="flex items-center justify-between border-b bg-card px-4 py-3 md:hidden">
          <div className="flex items-center gap-2">
            <img src={logoImg} alt="ACA" className="h-8 w-8 rounded-lg object-contain" />
            <div>
              <h1 className="text-lg font-bold text-foreground">ACA System</h1>
              {firstName && <p className="text-xs text-muted-foreground">Welcome, {firstName}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setAccountOpen(true)} title="My Account">
              <User className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSignOut} disabled={signingOut}>
              {signingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b bg-card px-2 py-2 md:hidden">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                location.pathname === item.href
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent"
              )}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </nav>
        <main className="flex-1 overflow-y-auto bg-background p-4 md:p-8">
          {children}
        </main>
      </div>
      <AccountDialog open={accountOpen} onOpenChange={setAccountOpen} />
    </div>
  );
};

export default DashboardLayout;
